import React from 'react';
import { 
  Card, 
  Box, 
  Typography, 
  IconButton, 
  useTheme, 
  alpha,
  Divider,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import { 
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  SaveAlt as ExportIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
);

/**
 * A reusable chart component for ESDL dashboard
 * 
 * @param {Object} props
 * @param {string} props.title - The title of the chart
 * @param {string} props.subtitle - The subtitle or description
 * @param {string} props.chartType - Type of chart ('line', 'bar', 'doughnut', 'pie')
 * @param {Object} props.chartData - The data for the chart in Chart.js format
 * @param {Object} props.chartOptions - Options for the chart
 * @param {function} props.onRefresh - Function to call when refresh button is clicked
 * @param {Array} props.menuOptions - Array of menu options with label and action
 * @param {boolean} props.loading - Whether the chart is in loading state
 * @param {number} props.height - Height of the chart (default 300px)
 */
const EsdlSummaryChart = ({
  title,
  subtitle,
  chartType = 'line',
  chartData,
  chartOptions = {},
  onRefresh,
  menuOptions = [],
  loading = false,
  height = 300
}) => {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const open = Boolean(menuAnchorEl);
  
  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleMenuItemClick = (action) => {
    handleMenuClose();
    if (typeof action === 'function') {
      action();
    }
  };
  
  // Default chart options with dark/light mode support
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
            family: theme.typography.fontFamily
          }
        }
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        cornerRadius: 8,
        titleFont: {
          size: 13,
          weight: 'bold',
          family: theme.typography.fontFamily
        },
        bodyFont: {
          size: 12,
          family: theme.typography.fontFamily
        },
        bodySpacing: 6
      }
    },
    scales: chartType === 'line' || chartType === 'bar' ? {
      x: {
        grid: {
          color: alpha(theme.palette.divider, 0.1),
          drawBorder: false
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 10,
            family: theme.typography.fontFamily
          }
        }
      },
      y: {
        grid: {
          color: alpha(theme.palette.divider, 0.1),
          drawBorder: false
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 10,
            family: theme.typography.fontFamily
          }
        }
      }
    } : undefined
  };
  
  // Merge default options with provided options
  const mergedOptions = { ...defaultOptions, ...chartOptions };
  
  // Render the appropriate chart based on chartType
  const renderChart = () => {
    if (!chartData) {
      return (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height={height} 
          sx={{ color: theme.palette.text.secondary }}
        >
          <InfoIcon sx={{ mr: 1 }} />
          <Typography variant="body2">No data available</Typography>
        </Box>
      );
    }
    
    switch (chartType) {
      case 'line':
        return <Line data={chartData} options={mergedOptions} height={height} />;
      case 'bar':
        return <Bar data={chartData} options={mergedOptions} height={height} />;
      case 'doughnut':
        return <Doughnut data={chartData} options={mergedOptions} height={height} />;
      case 'pie':
        return <Pie data={chartData} options={mergedOptions} height={height} />;
      default:
        return <Line data={chartData} options={mergedOptions} height={height} />;
    }
  };
  
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        backdropFilter: 'blur(8px)',
        background: alpha(theme.palette.background.paper, 0.8),
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Loading overlay */}
      {loading && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            zIndex: 10 
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
      
      {/* Header */}
      <Box p={2.5} pb={1.5}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" fontWeight="medium" color="text.primary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box>
            {onRefresh && (
              <IconButton 
                size="small" 
                onClick={onRefresh}
                sx={{ mr: 0.5 }}
                aria-label="Refresh data"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
            {menuOptions.length > 0 && (
              <>
                <IconButton
                  size="small"
                  onClick={handleMenuClick}
                  aria-label="More options"
                  aria-controls={open ? 'chart-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
                <Menu
                  id="chart-menu"
                  anchorEl={menuAnchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'chart-menu-button',
                  }}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      minWidth: 180,
                      overflow: 'visible',
                      borderRadius: 2,
                      mt: 1,
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {menuOptions.map((option, index) => (
                    <MenuItem 
                      key={index} 
                      onClick={() => handleMenuItemClick(option.action)}
                      sx={{ py: 1, px: 2, fontSize: '0.875rem' }}
                    >
                      {option.icon && (
                        <Box component="span" display="inline-flex" mr={1.5} color="text.secondary">
                          {option.icon}
                        </Box>
                      )}
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ opacity: 0.5 }} />
      
      {/* Chart */}
      <Box p={2.5} sx={{ height: `${height}px` }}>
        {renderChart()}
      </Box>
    </Card>
  );
};

export default EsdlSummaryChart; 